/**
 * Adds rail-only metadata while leaving the producer's option and data objects
 * untouched. This is the only route by which the lifecycle controller learns
 * about producer intent that React-Toastify's ToastItem does not retain.
 */
export function withToastRailMetadata(options = {}, metadata = {}) {
  const data = options.data || {};
  const toastRail = data.toastRail || {};

  return {
    ...options,
    data: {
      ...data,
      toastRail: {
        ...toastRail,
        ...metadata,
      },
    },
  };
}

/**
 * Makes one toast's `onClose` fire at most once.
 *
 * React-Toastify v11 registers a raw `document.addEventListener("pointerup", u)`
 * when its drag starts, independent of React's synthetic dispatch. React's
 * delegated listener lives on the root container — an ancestor closer to the
 * event target than `document` — so the rail's own `onPointerUp` resolves first
 * and the library's `u()` then calls `t.closeToast(true)` unconditionally. Both
 * land in the library's `removeToast`, which invokes `props.onClose(reason)`
 * with no `isActive` check, so one fast horizontal swipe reaches the producer
 * twice. The library's second call never passes through this module, so the only
 * place that can absorb it is the producer's own callback.
 *
 * The guard is created per call, so it is per toast: two toasts built from the
 * same producer function each get their own latch and cannot silence each other.
 * A producer that supplied nothing does not gain an `onClose`.
 */
export function withToastRailCloseGuard(options = {}) {
  const producerOnClose = options.onClose;
  if (typeof producerOnClose !== "function") return options;

  let closed = false;

  return {
    ...options,
    onClose: (...args) => {
      if (closed) return undefined;
      closed = true;
      return producerOnClose(...args);
    },
  };
}

/**
 * Builds the toast options for the native bridge producers in
 * `usePostMessages.js`, translating the bridge's own explicit choices into the
 * rail metadata that React-Toastify's `ToastItem` cannot carry.
 *
 * The option chain deliberately mirrors the pre-rail bridge behaviour value for
 * value; the only additions are `data.toastRail`, the `draggable: false` that
 * `canDismiss: false` always implied, the close-once guard above, and — only when
 * `isMobileApp` — suppression of a producer-supplied close button.
 */
export function buildNativeToastOptions(eventData = {}, existingOptions = {}, { isMobileApp = false } = {}) {
  const event = eventData || {};
  const options = { ...existingOptions };
  const metadata = {};

  // The rail contract is "no close button". The container asks for
  // `closeButton: false`, but react-toastify lets a per-toast value override the
  // container, so the sticky sync toast's own `closeButton: true` would otherwise
  // put a close button on a capsule. Suppressed on the rail only: a phone browser
  // keeps whatever the producer asked for, and a producer that asked for nothing
  // does not gain the key.
  if (isMobileApp && options.closeButton !== undefined && options.closeButton !== false) {
    options.closeButton = false;
  }

  if (event.toastId) {
    options.toastId = event.toastId;
    // Pre-existing bridge behaviour: an identified native toast is sticky. That
    // is a supplied auto-close, so it is marked as one — otherwise the rail
    // controller would retime a sync toast to 3s and it would vanish mid-upload.
    options.autoClose = false;
    metadata.explicitToastId = true;
    metadata.preserveAutoClose = true;
  }

  if (event.progress !== undefined) options.progress = event.progress;

  if (event.canDismiss === false) {
    options.closeButton = false;
    options.closeOnClick = false;
    // v11's horizontal distance drag is the other dismissal path, so the bridge
    // has to disable it too; the metadata disables the rail's velocity path.
    options.draggable = false;
    metadata.canDismiss = false;
  }

  if (event.autoClose !== undefined) {
    options.autoClose = event.autoClose;
    metadata.preserveAutoClose = true;
  }

  // Only a producer-supplied action is ever carried. Nothing here fabricates one.
  if (event.action != null) metadata.action = event.action;

  return withToastRailCloseGuard(withToastRailMetadata(options, metadata));
}
